import { useEffect, useMemo, useState, useCallback } from "react";
import { Calendar, CaretLeft, CaretRight, X, FloppyDisk, Trash, Users, CircleNotch, PencilSimple } from "@phosphor-icons/react";
import axios from "../../utils/axiosInstance";
import { useAuth } from "../../contexts/AuthContext";

const API = `${window.location.origin}/api`;

const WEEKDAYS = ["Pn", "Wt", "Śr", "Cz", "Pt", "So", "Nd"];

// ============== date helpers ==============
const isoDate = (d) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

function buildMonthGrid(anchor) {
  // Monday-first grid, 6 rows × 7 days
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const startOffset = (first.getDay() + 6) % 7; // 0=Mon..6=Sun
  const gridStart = new Date(first);
  gridStart.setDate(first.getDate() - startOffset);
  const days = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    days.push(d);
  }
  return days;
}


export default function DyzuryTab() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [anchor, setAnchor] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [dyzury, setDyzury] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { date, employee_ids, notes }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/dyzury`, { params: { month: monthKey(anchor) } });
      setDyzury(data || []);
    } catch (e) {
      console.error(e);
      setDyzury([]);
    } finally { setLoading(false); }
  }, [anchor]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/employees`);
        setEmployees((data || []).filter((e) => e.role === "employee" || !e.role));
      } catch { setEmployees([]); }
    })();
  }, [isAdmin]);

  const days = useMemo(() => buildMonthGrid(anchor), [anchor]);
  const byDate = useMemo(() => {
    const m = {};
    for (const d of dyzury) m[d.date] = d;
    return m;
  }, [dyzury]);

  const monthLabel = anchor.toLocaleDateString("pl-PL", { month: "long", year: "numeric" });

  const shift = (n) => {
    const d = new Date(anchor);
    d.setMonth(d.getMonth() + n);
    setAnchor(d);
  };

  const openEdit = (d) => {
    if (!isAdmin) return;
    if (d.getDay() !== 6) return; // Only Saturday
    const key = isoDate(d);
    const existing = byDate[key];
    setEditing({
      date: key,
      employee_ids: existing?.employee_ids || [],
      notes: existing?.notes || "",
    });
  };

  const toggleEmp = (uid) => {
    setEditing((prev) => {
      const ids = new Set(prev.employee_ids);
      if (ids.has(uid)) ids.delete(uid); else ids.add(uid);
      return { ...prev, employee_ids: [...ids] };
    });
  };

  const save = async () => {
    if (!editing) return;
    try {
      await axios.put(`${API}/dyzury`, {
        date: editing.date,
        employee_ids: editing.employee_ids,
        notes: editing.notes || null,
      });
      setEditing(null);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Nie udało się zapisać");
    }
  };

  const removeAll = async () => {
    if (!editing) return;
    if (!window.confirm("Usunąć dyżur na tę sobotę?")) return;
    try {
      await axios.delete(`${API}/dyzury/${editing.date}`);
      setEditing(null);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Nie udało się usunąć");
    }
  };

  const empName = (uid) => employees.find((e) => (e._id || e.id) === uid)?.imie_nazwisko || employees.find((e) => (e._id || e.id) === uid)?.name || uid;

  return (
    <div className="max-w-[1200px] mx-auto px-3 py-4 sm:px-6 sm:py-6 space-y-4" data-testid="dyzury-container">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl sm:text-3xl font-bold text-slate-800 flex items-center gap-2">
            <Calendar className="w-6 h-6 text-blue-700" weight="fill" />
            Dyżury sobotnie
          </h2>
          <p className="text-xs sm:text-base text-slate-600 mt-1">
            {isAdmin
              ? "Kliknij sobotę, aby przypisać pracowników na dyżur. Powiadomienia wysyłane wg ustawień."
              : "Twój grafik dyżurów sobotnich."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => shift(-1)} className="p-2 text-slate-600 hover:bg-slate-100 rounded-md" data-testid="dyzury-prev-month"><CaretLeft className="w-5 h-5" /></button>
          <div className="text-sm sm:text-base font-semibold text-slate-800 min-w-[140px] text-center capitalize">
            {monthLabel}
          </div>
          <button onClick={() => shift(1)} className="p-2 text-slate-600 hover:bg-slate-100 rounded-md" data-testid="dyzury-next-month"><CaretRight className="w-5 h-5" /></button>
        </div>
      </div>

      {/* Calendar */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-3 sm:p-4">
        {loading ? (
          <div className="text-center py-10 text-slate-400"><CircleNotch className="w-8 h-8 animate-spin mx-auto" /></div>
        ) : (
          <>
            <div className="grid grid-cols-7 gap-1 mb-2">
              {WEEKDAYS.map((w, i) => (
                <div key={w} className={`text-center text-xs font-semibold ${i === 5 ? "text-blue-700" : i === 6 ? "text-red-500" : "text-slate-500"}`}>{w}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {days.map((d) => {
                const inMonth = d.getMonth() === anchor.getMonth();
                const isSat = d.getDay() === 6;
                const key = isoDate(d);
                const dz = byDate[key];
                const isToday = key === isoDate(new Date());
                return (
                  <button
                    key={key}
                    onClick={() => isSat && openEdit(d)}
                    disabled={!isSat}
                    data-testid={isSat ? `dyzury-cell-${key}` : undefined}
                    className={`min-h-[68px] sm:min-h-[86px] p-1.5 rounded-md border text-left transition-colors ${
                      !inMonth ? "border-transparent text-slate-300 bg-slate-50/40" :
                      isSat ? (dz ? "border-blue-500 bg-blue-50 hover:bg-blue-100 cursor-pointer" : "border-slate-200 hover:bg-slate-50 cursor-pointer") :
                      "border-slate-100 bg-white cursor-default"
                    } ${isToday ? "ring-2 ring-blue-400" : ""}`}
                  >
                    <div className={`text-xs font-semibold ${isSat && inMonth ? "text-blue-700" : d.getDay() === 6 ? "text-red-400" : ""}`}>{d.getDate()}</div>
                    {dz && dz.employees && (
                      <>
                        {/* Mobile: compact count chip (names are unreadable when truncated in narrow cells) */}
                        <div className="sm:hidden mt-1">
                          <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold bg-blue-700 text-white px-1.5 py-0.5 rounded">
                            <Users className="w-2.5 h-2.5" weight="fill" />
                            {dz.employees.length}
                          </span>
                        </div>
                        {/* Desktop: name pills as before */}
                        <div className="hidden sm:block mt-1 space-y-0.5">
                          {dz.employees.slice(0, 3).map((e) => (
                            <div key={e.id} className="text-xs bg-blue-700 text-white px-1 py-0.5 rounded truncate">
                              {e.name}
                            </div>
                          ))}
                          {dz.employees.length > 3 && (
                            <div className="text-[10px] text-blue-700">+{dz.employees.length - 3}</div>
                          )}
                        </div>
                      </>
                    )}
                    {!dz && isSat && inMonth && isAdmin && (
                      <div className="text-[10px] text-slate-400 mt-2 italic">przypisz</div>
                    )}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Mobile-only agenda: full names per Saturday (calendar cells only show count on mobile) */}
      <div className="sm:hidden bg-white rounded-xl border border-slate-200 shadow-sm p-3">
        <div className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-2">
          <Calendar className="w-4 h-4 text-blue-700" weight="fill" />
          Kto dyżuruje w {monthLabel.split(" ")[0].toLowerCase()}
        </div>
        {(() => {
          const monthSats = dyzury
            .filter((d) => {
              const dt = new Date(d.date);
              return dt.getFullYear() === anchor.getFullYear() && dt.getMonth() === anchor.getMonth();
            })
            .sort((a, b) => a.date.localeCompare(b.date));
          if (monthSats.length === 0) {
            return <div className="text-sm text-slate-500 italic">Brak zaplanowanych dyżurów w tym miesiącu.</div>;
          }
          return (
            <ul className="space-y-2">
              {monthSats.map((d) => {
                const dt = new Date(d.date);
                const day = String(dt.getDate()).padStart(2, "0");
                return (
                  <li
                    key={d.date}
                    className="flex items-start gap-2.5 p-2 rounded-md hover:bg-slate-50 active:bg-slate-100"
                    data-testid={`dyzury-agenda-${d.date}`}
                    onClick={() => isAdmin && openEdit(dt)}
                  >
                    <div className="shrink-0 w-11 h-11 rounded-md bg-blue-50 text-blue-700 flex flex-col items-center justify-center leading-tight">
                      <span className="text-[9px] font-semibold uppercase">So</span>
                      <span className="text-base font-bold">{day}</span>
                    </div>
                    <div className="flex-1 min-w-0 pt-0.5">
                      <div className="flex flex-wrap gap-1">
                        {(d.employees || []).map((e) => (
                          <span
                            key={e.id}
                            className="inline-block text-[11px] bg-blue-700 text-white px-2 py-0.5 rounded-full"
                          >
                            {e.name}
                          </span>
                        ))}
                      </div>
                      {d.notes && (
                        <div className="text-[11px] text-slate-500 mt-1 italic truncate">{d.notes}</div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          );
        })()}
      </div>

      {/* Upcoming list (employee view) */}
      {!isAdmin && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
          <div className="text-sm font-semibold text-slate-800 mb-2">Twoje najbliższe dyżury</div>
          {(() => {
            const uid = String(user?._id);
            const mine = dyzury
              .filter((d) => d.employee_ids?.includes(uid) && d.date >= isoDate(new Date()))
              .sort((a, b) => a.date.localeCompare(b.date));
            if (mine.length === 0) return <div className="text-sm text-slate-500">Brak zaplanowanych dyżurów w tym miesiącu.</div>;
            return (
              <ul className="space-y-1.5">
                {mine.map((d) => (
                  <li key={d.date} className="flex items-center gap-2 text-sm">
                    <div className="w-8 h-8 rounded-md bg-blue-50 text-blue-700 flex items-center justify-center font-semibold">
                      {new Date(d.date).getDate()}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-slate-800">{new Date(d.date).toLocaleDateString("pl-PL", { weekday: "long", day: "2-digit", month: "long" })}</div>
                      {d.notes && <div className="text-xs text-slate-500">{d.notes}</div>}
                    </div>
                  </li>
                ))}
              </ul>
            );
          })()}
        </div>
      )}

      {/* Edit modal (admin) */}
      {editing && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md flex flex-col max-h-[95vh]" data-testid="dyzury-edit-modal">
            <div className="flex items-center justify-between p-4 sm:p-6 pb-3 border-b border-slate-100 shrink-0">
              <div>
                <h3 className="text-base sm:text-lg font-bold text-slate-800">Dyżur — {new Date(editing.date).toLocaleDateString("pl-PL", { day: "2-digit", month: "long", year: "numeric" })}</h3>
                <p className="text-xs text-slate-500 mt-0.5">Wybierz pracowników na ten dyżur</p>
              </div>
              <button onClick={() => setEditing(null)} className="p-1 hover:bg-slate-100 rounded-md shrink-0"><X className="w-5 h-5" /></button>
            </div>

            <div className="p-4 sm:p-6 pt-3 space-y-3 overflow-y-auto grow">
              <div className="border border-slate-200 rounded-md">
                {employees.length === 0 ? (
                  <div className="text-sm text-slate-500 p-3 text-center">Brak pracowników</div>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {employees.map((e) => {
                      const uid = e._id || e.id;
                      const active = editing.employee_ids.includes(uid);
                      return (
                        <li key={uid}>
                          <label className={`flex items-center gap-2 px-3 py-2.5 cursor-pointer hover:bg-slate-50 ${active ? "bg-blue-50" : ""}`}>
                            <input
                              type="checkbox"
                              checked={active}
                              onChange={() => toggleEmp(uid)}
                              className="w-4 h-4 accent-blue-700"
                              data-testid={`dyzury-emp-${uid}`}
                            />
                            <span className="text-sm text-slate-800">{e.imie_nazwisko || e.name}</span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Notatka (opcjonalnie)</label>
                <textarea
                  value={editing.notes}
                  onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                  rows={2}
                  placeholder="np. dyżur pilny"
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                  data-testid="dyzury-notes"
                />
              </div>
              {editing.employee_ids.length > 0 && (
                <div className="text-xs text-slate-600">
                  Wybrani: <span className="font-medium">{editing.employee_ids.map(empName).join(", ")}</span>
                </div>
              )}
              <div className="flex justify-between gap-2 pt-2">
                <button
                  onClick={removeAll}
                  className="inline-flex items-center gap-1 px-3 py-2 text-red-600 hover:bg-red-50 rounded-md text-sm"
                  data-testid="dyzury-delete"
                >
                  <Trash className="w-4 h-4" /> Usuń
                </button>
                <div className="flex gap-2">
                  <button onClick={() => setEditing(null)} className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md text-sm">Anuluj</button>
                  <button
                    onClick={save}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-md text-sm font-medium"
                    data-testid="dyzury-save"
                  >
                    <FloppyDisk className="w-4 h-4" weight="fill" /> Zapisz
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
